import { TZDate } from "@date-fns/tz"
import { format } from "date-fns"

import { UK_TIMEZONE, ukWallClockToInstant } from "@/constants/sheriffOptions"

/**
 * A TZDate that stays in UK time when copied. date-fns v3 copies its input with
 * `new date.constructor(+date)`, which turns a plain TZDate into the browser's timezone;
 * fixing the zone in the constructor keeps every copy on UK time.
 */
class UKDate extends TZDate {
  constructor(...args: unknown[]) {
    if (args.length) super(...(args as [number]), UK_TIMEZONE)
    else super(Date.now(), UK_TIMEZONE)
  }
}

/**
 * date-fns `format` for an instant, rendered in UK time whatever the browser's timezone.
 * Use it for shift times, which are stored as UTC and always read as UK wall-clock times.
 */
export const ukFormat = (value: string | number | Date, pattern: string): string =>
  format(new UKDate(new Date(value).getTime()), pattern)

/**
 * The ISO instant for a `datetime-local` input value (YYYY-MM-DDTHH:mm, optionally with
 * seconds and milliseconds) read as UK time. Undefined if the input is empty or names a time
 * that doesn't exist in the UK.
 */
export const ukInputToIso = (value: string): string | undefined => {
  const [date, time] = value.split("T")
  if (!date || !time) return undefined
  const instant = ukWallClockToInstant(date, time)
  return Number.isNaN(instant.getTime()) ? undefined : instant.toISOString()
}
